import { useForm } from "@tanstack/react-form";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { Input } from "../../components/Input";
import { MoneyInput } from "../../components/MoneyInput";
import { InfoBox } from "../../components/InfoBox";
import { useGetStoreQuery } from "../../query/storesQuery";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import type { Money } from "@KassaroAB/shared/types/money";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { GiftCardStatus } from "@KassaroAB/shared/types/enums/GiftCardStatus";
import type { RestCreateGiftCard, RestGiftCard } from "@KassaroAB/shared/types/giftCard";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/dateUtils";

const ReadOnlyField = styled.div`
  padding: ${spacing.x2}px;
  background-color: ${colors.backgroundDark};
  border-radius: 4px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  font-family: monospace;
`;

interface GiftcardFormProps {
  giftcard?: RestGiftCard;
  onSubmit: (data: RestCreateGiftCard) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const GiftcardForm = ({
  giftcard,
  onSubmit,
  onCancel,
  isLoading,
}: GiftcardFormProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { data: store } = useGetStoreQuery(storeId);
  const storeCurrency = store?.currency || Currency.SEK;

  const form = useForm({
    defaultValues: {
      cardNumber: giftcard?.cardNumber ?? "",
      initialAmountAmount: giftcard?.initialAmount?.amount ? giftcard.initialAmount.amount / 100 : 0,
      remainingAmountAmount: giftcard?.remainingAmount?.amount ? giftcard.remainingAmount.amount / 100 : 0,
      validUntil: giftcard?.validUntil
        ? formatDate(giftcard.validUntil)
        : "",
    },
    onSubmit: ({ value }) => {
      const initialAmount: Money = {
        amount: value.initialAmountAmount * 100,
        currency: storeCurrency,
      };
      const remainingAmount: Money = {
        amount: value.remainingAmountAmount * 100,
        currency: storeCurrency,
      };
      let cardNumber = value.cardNumber;
      if (!cardNumber.startsWith("GC-")) {
        cardNumber = `GC-${cardNumber}`;
      }
      onSubmit({
        cardNumber,
        initialAmount,
        remainingAmount,
        validUntil: new Date(value.validUntil).toISOString(),
        status: GiftCardStatus.ACTIVE,
      });
    },
  });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <InfoBox variant="warning">
        {t("adminForms.warningNotBookkept")}
      </InfoBox>

      <form.Field name="cardNumber">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("giftcardForm.cardNumber")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="initialAmountAmount">
        {(field) => (
          <MoneyInput
            label={t("giftcardForm.initialAmount")}
            amount={field.state.value}
            currency={storeCurrency}
            onAmountChange={field.handleChange}
            onAmountBlur={field.handleBlur}
            required
          />
        )}
      </form.Field>

      <form.Field name="remainingAmountAmount">
        {(field) => (
          <MoneyInput
            label={t("giftcardForm.remainingAmount")}
            amount={field.state.value}
            currency={storeCurrency}
            onAmountChange={field.handleChange}
            onAmountBlur={field.handleBlur}
            required
          />
        )}
      </form.Field>

      <form.Field name="validUntil">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("giftcardForm.validUntil")}</FormLabel>
            <Input
              type="date"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
          </FormGroup>
        )}
      </form.Field>

      {giftcard && (
        <FormGroup>
          <FormLabel>{t("giftcardForm.createdAt")}</FormLabel>
          <ReadOnlyField>
            {new Date(giftcard.createdAt).toLocaleString()}
          </ReadOnlyField>
        </FormGroup>
      )}

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("common.saving") : giftcard ? t("common.update") : t("common.create")}
        </Button>
      </FormActions>
    </Form>
  );
};
